/**
 * Authentication Validation Schemas
 * Zod schemas for authentication forms
 */

import { z } from "zod"

/**
 * Login schema
 * Validates user login input
 */
export const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters long"),
})

export type LoginFormData = z.infer<typeof loginSchema>

/**
 * Signup schema
 * Validates user registration input with all profile fields
 */
export const signupSchema = z
  .object({
    email: z.string().min(1, "Email is required").email("Please enter a valid email"),
    name: z.string().min(2, "Name must be at least 2 characters").max(100),
    username: z
      .string()
      .min(3, "Username must be at least 3 characters")
      .max(50, "Username must not exceed 50 characters")
      .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
    city: z.string().nullable().optional(),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    referralCode: z.string().optional(),
    streetAddress: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    zipcode: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
  })
  .refine(data => !!(data.city || data.state || data.country), {
    message: "Please provide your location.",
    path: ["city"],
  })

export type SignupFormData = z.infer<typeof signupSchema>

/**
 * Google Signup schema (password not required)
 * For users signing up via Google OAuth
 * Defined independently to avoid Zod's limitation: cannot .extend() on schemas with .refine()
 */
export const googleSignupSchema = z
  .object({
    email: z.string().min(1, "Email is required").email("Please enter a valid email"),
    name: z.string().min(2, "Name must be at least 2 characters").max(100),
    username: z
      .string()
      .min(3, "Username must be at least 3 characters")
      .max(50, "Username must not exceed 50 characters")
      .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
    city: z.string().nullable().optional(),
    password: z.string().optional(),
    referralCode: z.string().optional(),
    streetAddress: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    zipcode: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
  })
  .refine(data => !!(data.city || data.state || data.country), {
    message: "City is required",
    path: ["city"],
  })

export type GoogleSignupFormData = z.infer<typeof googleSignupSchema>

/**
 * Verify email schema
 * Validates email verification OTP input
 */
export const verifyEmailSchema = z.object({
  code: z
    .string()
    .length(6, "Verification code must be 6 digits")
    .regex(/^\d+$/, "Verification code must contain only numbers"),
})

export type VerifyEmailFormData = z.infer<typeof verifyEmailSchema>

/**
 * Resend verification email schema
 */
export const resendVerificationSchema = z.object({
  email: z.string().email("Please provide a valid email address"),
})

export type ResendVerificationFormData = z.infer<typeof resendVerificationSchema>

/**
 * Forgot password schema
 */
export const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email"),
})

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

/**
 * Reset password schema
 */
export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters long")
      .regex(/^(?=.*[A-Za-z])(?=.*\d)/, "Password must contain both letters and numbers"),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>
